/**
 * Seed data for Cover Club — CoverEntry (book reference + engagement),
 * seeded CoverReaction counts, CoverComment threads, and CoverVote battles.
 * Stands in for the eventual API response; every record uses id references
 * rather than duplicating book fields (see utils/bookCatalog.js).
 */

const DAY = 24 * 60 * 60 * 1000;
const now = Date.now();

export const COVER_ENTRIES = [
  {
    bookId: 'book_night_circus',
    style: 'Illustrated', mood: 'Whimsical', palette: 'Monochrome + Coral', era: 'Modern Classic',
    createdAt: now - 40 * DAY, featured: true,
    reactionCounts: { stunning: 214, caught_eye: 98, beautiful_design: 156, fits_story: 61, instant_tbr: 43 },
    collections: ['most_loved', 'eye_catching'],
    comments: [
      { id: 'cc_1', author: 'mira.reads', text: "The tent stripes on the spine are what got me — didn't even read the blurb.", createdAt: now - 30 * DAY },
      { id: 'cc_2', author: 'chapterzero', text: 'This cover is the whole reason it sold out at my local shop.', createdAt: now - 12 * DAY },
    ],
  },
  {
    bookId: 'book_babel',
    style: 'Typographic', mood: 'Dramatic', palette: 'Warm Gold', era: 'Contemporary',
    createdAt: now - 38 * DAY, featured: true,
    reactionCounts: { stunning: 187, caught_eye: 140, beautiful_design: 122, fits_story: 88, instant_tbr: 52 },
    collections: ['most_loved', 'best_fantasy'],
    comments: [
      { id: 'cc_3', author: 'quietpages', text: 'The tower silhouette in the negative space is such a quiet flex.', createdAt: now - 20 * DAY },
    ],
  },
  {
    bookId: 'book_piranesi',
    style: 'Minimalist', mood: 'Eerie', palette: 'Cool Blue', era: 'Contemporary',
    createdAt: now - 34 * DAY, featured: false,
    reactionCounts: { stunning: 96, caught_eye: 201, beautiful_design: 84, fits_story: 40, instant_tbr: 19 },
    collections: ['minimalist', 'eye_catching'],
    comments: [],
  },
  {
    bookId: 'book_circe',
    style: 'Illustrated', mood: 'Mythic', palette: 'Sunset Yellow', era: 'Modern Classic',
    createdAt: now - 33 * DAY, featured: true,
    reactionCounts: { stunning: 260, caught_eye: 88, beautiful_design: 190, fits_story: 120, instant_tbr: 71 },
    collections: ['most_loved', 'cover_crushes'],
    comments: [
      { id: 'cc_4', author: 'nightowlnotes', text: 'That profile silhouette is iconic at this point.', createdAt: now - 18 * DAY },
    ],
  },
  {
    bookId: 'book_cerulean_sea',
    style: 'Pastel', mood: 'Cozy', palette: 'Soft Sage', era: 'Contemporary',
    createdAt: now - 30 * DAY, featured: false,
    reactionCounts: { stunning: 133, caught_eye: 71, beautiful_design: 144, fits_story: 66, instant_tbr: 38 },
    collections: ['pastel', 'cover_crushes'],
    comments: [],
  },
  {
    bookId: 'book_legends_lattes',
    style: 'Illustrated', mood: 'Cozy', palette: 'Warm Coral', era: 'Contemporary',
    createdAt: now - 27 * DAY, featured: false,
    reactionCounts: { stunning: 102, caught_eye: 64, beautiful_design: 98, fits_story: 91, instant_tbr: 60 },
    collections: ['cover_crushes', 'instant_tbr_collection'],
    comments: [
      { id: 'cc_5', author: 'mira.reads', text: 'I want this as a poster honestly.', createdAt: now - 9 * DAY },
    ],
  },
  {
    bookId: 'book_study_drowning',
    style: 'Dark Academia', mood: 'Moody', palette: 'Deep Blue', era: 'Contemporary',
    createdAt: now - 24 * DAY, featured: true,
    reactionCounts: { stunning: 178, caught_eye: 92, beautiful_design: 133, fits_story: 77, instant_tbr: 64 },
    collections: ['dark_academia', 'eye_catching'],
    comments: [],
  },
  {
    bookId: 'book_fourth_wing',
    style: 'Illustrated', mood: 'Epic', palette: 'Deep Coral', era: 'Contemporary',
    createdAt: now - 22 * DAY, featured: true,
    reactionCounts: { stunning: 301, caught_eye: 110, beautiful_design: 210, fits_story: 140, instant_tbr: 188 },
    collections: ['best_fantasy', 'instant_tbr_collection'],
    comments: [
      { id: 'cc_6', author: 'chapterzero', text: 'The dragon foil detail on the hardback is unreal in person.', createdAt: now - 6 * DAY },
      { id: 'cc_7', author: 'quietpages', text: 'Added to TBR purely because of this cover, no regrets.', createdAt: now - 3 * DAY },
    ],
  },
  {
    bookId: 'book_yellowface',
    style: 'Typographic', mood: 'Sharp', palette: 'Bold Yellow', era: 'Contemporary',
    createdAt: now - 19 * DAY, featured: false,
    reactionCounts: { stunning: 84, caught_eye: 176, beautiful_design: 70, fits_story: 55, instant_tbr: 29 },
    collections: ['eye_catching'],
    comments: [],
  },
  {
    bookId: 'book_silent_patient',
    style: 'Minimalist', mood: 'Tense', palette: 'Cool Blue', era: 'Contemporary',
    createdAt: now - 17 * DAY, featured: false,
    reactionCounts: { stunning: 61, caught_eye: 140, beautiful_design: 58, fits_story: 82, instant_tbr: 24 },
    collections: ['minimalist'],
    comments: [],
  },
  {
    bookId: 'book_hail_mary',
    style: 'Illustrated', mood: 'Adventurous', palette: 'Deep Sage', era: 'Contemporary',
    createdAt: now - 15 * DAY, featured: false,
    reactionCounts: { stunning: 122, caught_eye: 90, beautiful_design: 101, fits_story: 66, instant_tbr: 40 },
    collections: ['cover_crushes'],
    comments: [],
  },
  {
    bookId: 'disc_tomorrow',
    style: 'Typographic', mood: 'Nostalgic', palette: 'Warm Coral', era: 'Y2K',
    createdAt: now - 13 * DAY, featured: true,
    reactionCounts: { stunning: 149, caught_eye: 65, beautiful_design: 130, fits_story: 58, instant_tbr: 47 },
    collections: ['most_loved'],
    comments: [],
  },
  {
    bookId: 'disc_atlas_six',
    style: 'Dark Academia', mood: 'Moody', palette: 'Deep Blue', era: 'Contemporary',
    createdAt: now - 11 * DAY, featured: false,
    reactionCounts: { stunning: 95, caught_eye: 58, beautiful_design: 87, fits_story: 40, instant_tbr: 33 },
    collections: ['dark_academia'],
    comments: [],
  },
  {
    bookId: 'disc_song_achilles',
    style: 'Illustrated', mood: 'Mythic', palette: 'Sunset Yellow', era: 'Modern Classic',
    createdAt: now - 9 * DAY, featured: true,
    reactionCounts: { stunning: 205, caught_eye: 77, beautiful_design: 168, fits_story: 110, instant_tbr: 82 },
    collections: ['most_loved', 'best_fantasy'],
    comments: [
      { id: 'cc_8', author: 'nightowlnotes', text: 'The laurel-leaf typography detail is so understated and perfect.', createdAt: now - 4 * DAY },
    ],
  },
  {
    bookId: 'disc_bear_nightingale',
    style: 'Illustrated', mood: 'Wintry', palette: 'Cool Blue', era: 'Contemporary',
    createdAt: now - 8 * DAY, featured: false,
    reactionCounts: { stunning: 118, caught_eye: 52, beautiful_design: 99, fits_story: 71, instant_tbr: 45 },
    collections: ['best_fantasy'],
    comments: [],
  },
  {
    bookId: 'disc_klara_sun',
    style: 'Minimalist', mood: 'Quiet', palette: 'Soft Sage', era: 'Contemporary',
    createdAt: now - 6 * DAY, featured: false,
    reactionCounts: { stunning: 73, caught_eye: 44, beautiful_design: 80, fits_story: 39, instant_tbr: 21 },
    collections: ['minimalist', 'pastel'],
    comments: [],
  },
  {
    bookId: 'disc_iron_widow',
    style: 'Illustrated', mood: 'Epic', palette: 'Deep Coral', era: 'Contemporary',
    createdAt: now - 5 * DAY, featured: true,
    reactionCounts: { stunning: 167, caught_eye: 91, beautiful_design: 140, fits_story: 76, instant_tbr: 99 },
    collections: ['eye_catching', 'instant_tbr_collection'],
    comments: [],
  },
  {
    bookId: 'disc_this_time',
    style: 'Pastel', mood: 'Nostalgic', palette: 'Soft Yellow', era: 'Y2K',
    createdAt: now - 3 * DAY, featured: false,
    reactionCounts: { stunning: 88, caught_eye: 40, beautiful_design: 95, fits_story: 42, instant_tbr: 30 },
    collections: ['pastel', 'cover_crushes'],
    comments: [],
  },
  {
    bookId: 'disc_locked_doors',
    style: 'Illustrated', mood: 'Whimsical', palette: 'Warm Sage', era: 'Modern Classic',
    createdAt: now - 1 * DAY, featured: false,
    reactionCounts: { stunning: 140, caught_eye: 62, beautiful_design: 128, fits_story: 70, instant_tbr: 55 },
    collections: ['best_fantasy', 'cover_crushes'],
    comments: [],
  },
];

export const BATTLES = [
  {
    id: 'battle_wing_widow',
    prompt: 'Which cover would make you pick up the book?',
    context: 'Two fantasy leads, two very different cover philosophies.',
    a: 'book_fourth_wing',
    b: 'disc_iron_widow',
    votes: { a: 341, b: 288 },
  },
  {
    id: 'battle_circe_achilles',
    prompt: 'Which cover would make you pick up the book?',
    context: "Madeline Miller's two mythic retellings, side by side.",
    a: 'book_circe',
    b: 'disc_song_achilles',
    votes: { a: 402, b: 379 },
  },
  {
    id: 'battle_minimal_illustrated',
    prompt: 'Minimalist or illustrated — which wins you over?',
    context: 'Two takes on a quiet, literary mystery.',
    a: 'book_piranesi',
    b: 'book_night_circus',
    votes: { a: 210, b: 267 },
  },
  {
    id: 'battle_pastel_dark',
    prompt: 'Which mood pulls you in more?',
    context: 'Pastel cozy vs. dark academia moody — pick your vibe.',
    a: 'book_cerulean_sea',
    b: 'disc_atlas_six',
    votes: { a: 233, b: 198 },
  },
];
